import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  DownloadCloud, Tags, FolderOpen, ClipboardList, GraduationCap,
  Loader2, Info, CheckCircle2, Save,
} from "lucide-react";

const SECTIONS = [
  { key: "plans", title: "Pricing & Plans", from: "GHL Products & Prices", icon: Tags, scope: "products.readonly, products/prices.readonly", to: "Pricing & Plans" },
  { key: "resources", title: "Resources", from: "GHL Media Library", icon: FolderOpen, scope: "medias.readonly", to: "Resources" },
  { key: "quizzes", title: "Quizzes & Surveys", from: "GHL Surveys", icon: ClipboardList, scope: "surveys.readonly", to: "Quizzes" },
];

export default function ImportCenter() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState({ plans: [], resources: [], quizzes: [] });
  const [running, setRunning] = useState("");

  const { data, isLoading, isError } = useQuery({ queryKey: ["import-available"], queryFn: async () => (await api.get("/import/available")).data });
  const { data: config } = useQuery({ queryKey: ["import-config"], queryFn: async () => (await api.get("/import/config")).data });

  const connected = !!data?.connected;

  useEffect(() => {
    if (config && Object.keys(config).length) {
      setSelected((s) => ({ ...s, ...config }));
    }
  }, [config]);

  const toggle = (section, id) =>
    setSelected((s) => {
      const list = s[section] || [];
      return { ...s, [section]: list.includes(id) ? list.filter((x) => x !== id) : [...list, id] };
    });

  const saveConfig = async () => {
    try {
      await api.post("/import/config", selected);
      toast.success("Import selections saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const runImport = async (section) => {
    setRunning(section);
    try {
      const { data: res } = await api.post("/import/run", { source: section, ids: selected[section] || [] });
      toast.success(`Imported ${res.imported} item(s)`);
      qc.invalidateQueries();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setRunning("");
    }
  };

  return (
    <div data-testid="import-page">
      <PageHeader
        title="Import from GHL"
        subtitle="Choose what to pull into each section of your dashboard. You can select now and import live once your GHL token is connected."
        action={
          <Button data-testid="save-import-config" onClick={saveConfig} className="rounded-full bg-navy-900 px-6 text-white hover:bg-navy-800">
            <Save className="mr-2 h-4 w-4" /> Save selections
          </Button>
        }
      />

      {/* connection banner */}
      <Card className={`mb-6 flex items-start gap-3 rounded-2xl border p-5 ${connected ? "border-emerald-200 bg-emerald-50" : "border-gold-100 bg-gold-50"}`} data-testid="import-connection-banner">
        {connected ? <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-600" /> : <Info className="mt-0.5 h-5 w-5 text-gold-600" />}
        <p className="text-sm text-navy-700">
          {connected
            ? "GoHighLevel is connected — items below are your live sub-account data. Select and Import to pull them in."
            : "Not connected yet — showing sample items so you can decide what to pull. Selections are saved and the live Import buttons activate once you connect your token in Settings → GHL Connection."}
        </p>
      </Card>

      {isLoading ? (
        <div className="flex items-center gap-3 text-navy-500"><Loader2 className="h-5 w-5 animate-spin text-gold-500" /> Loading…</div>
      ) : isError ? (
        <Card className="rounded-2xl border-navy-900/5 bg-white p-10 text-center text-navy-500" data-testid="import-error">
          Couldn't load import options. Please refresh the page.
        </Card>
      ) : (
        <div className="grid gap-6 lg:grid-cols-3">
          {SECTIONS.map((sec) => {
            const items = data?.[sec.key] || [];
            const chosen = selected[sec.key] || [];
            return (
              <Card key={sec.key} data-testid={`import-section-${sec.key}`} className="flex flex-col rounded-2xl border-navy-900/5 bg-white p-6 shadow-ambient">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gold-50"><sec.icon className="h-5 w-5 text-gold-600" /></div>
                  <div>
                    <h3 className="font-serif text-lg font-bold text-navy-900">{sec.title}</h3>
                    <p className="text-xs text-navy-400">from {sec.from}</p>
                  </div>
                </div>

                <div className="mt-4 flex-1 space-y-2">
                  {items.length === 0 ? (
                    <p className="text-sm text-navy-400">No items found.</p>
                  ) : (
                    items.map((it) => (
                      <label key={it.id} data-testid={`import-item-${it.id}`} className="flex cursor-pointer items-center gap-3 rounded-lg border border-navy-100 bg-ivory px-3 py-2 text-sm text-navy-700 hover:border-gold-300">
                        <Checkbox checked={chosen.includes(it.id)} onCheckedChange={() => toggle(sec.key, it.id)} />
                        <span className="flex-1 truncate">{it.name}</span>
                        {typeof it.price !== "undefined" && <Badge className="border-0 bg-gold-50 text-gold-700">${it.price}</Badge>}
                      </label>
                    ))
                  )}
                </div>

                <Button
                  data-testid={`run-import-${sec.key}`}
                  disabled={!connected || running === sec.key || chosen.length === 0}
                  onClick={() => runImport(sec.key)}
                  className="mt-5 w-full rounded-full bg-gold-500 text-white hover:bg-gold-600 disabled:opacity-50"
                  title={!connected ? "Connect GHL to import live items" : ""}
                >
                  {running === sec.key ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <DownloadCloud className="mr-2 h-4 w-4" />}
                  {connected ? `Import ${chosen.length ? `(${chosen.length})` : "selected"}` : "Connect GHL to import"}
                </Button>
                <p className="mt-2 text-center text-[11px] text-navy-400">Scope: {sec.scope}</p>
              </Card>
            );
          })}
        </div>
      )}

      {/* Courses note */}
      <Card className="mt-6 flex items-start gap-3 rounded-2xl border-navy-900/5 bg-white p-5 shadow-ambient">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-navy-50"><GraduationCap className="h-5 w-5 text-navy-500" /></div>
        <p className="text-sm text-navy-600">
          <b className="text-navy-900">Courses</b> aren't exposed by GoHighLevel's API for external apps — that's the limitation you hit. Build them natively under <b>Courses</b> for full flexibility.
        </p>
      </Card>
    </div>
  );
}
